import { useEffect } from "react";
import { matchPath, useLocation } from "react-router";

export interface RouteTitle {
  /** Path pattern in react-router syntax (e.g. "/config/projects/:id"). */
  path: string;
  /** Human title; empty string skips writing (e.g. transient redirect routes). */
  title: string;
}

export interface DocumentTitleEffectProps {
  /**
   * Ordered list of path patterns + titles. The first pattern that matches the
   * current `location.pathname` wins, so list more-specific patterns first.
   */
  routeTitles: ReadonlyArray<RouteTitle>;
  /** Appended after the matched title (e.g. " | Simplicity Admin"). */
  suffix: string;
  /** Title used when no pattern matches the current location. */
  fallback: string;
}

/**
 * Writes `document.title` from the active route's static title on every navigation — WCAG 2.4.2
 * (Page Titled), SIM-1398. Per-page `useDocumentTitle(name)` calls run after this and override with
 * the hydrated entity name; the early-exit guard keeps it a no-op when the title has not changed.
 */
export function DocumentTitleEffect({ routeTitles, suffix, fallback }: DocumentTitleEffectProps) {
  const location = useLocation();
  useEffect(() => {
    const match = routeTitles.find((r) =>
      matchPath({ path: r.path, end: true }, location.pathname),
    );
    const baseTitle = match?.title ?? "";
    const next = baseTitle ? `${baseTitle}${suffix}` : fallback;
    if (typeof document === "undefined") return;
    if (document.title !== next) document.title = next;
  }, [routeTitles, location.pathname, suffix, fallback]);
  return null;
}
